import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PortalLayout } from "../../components/portal/PortalLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { Package, ShoppingBag, Boxes, ArrowRight, PlusCircle } from "lucide-react";

export default function PortalDashboard() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Dashboard | Sambiva Trade"; }, []);
  const { data } = useQuery({ queryKey: ["trade-dashboard"], queryFn: async () => (await api.get("/trade/dashboard")).data });

  const cards = [
    { label: "Open Orders", value: data?.open_orders ?? "—", icon: Package, color: "text-sambiva-red bg-sambiva-red/10" },
    { label: "Total Orders", value: data?.total_orders ?? "—", icon: ShoppingBag, color: "text-sambiva-green bg-sambiva-green/10" },
    { label: "Available Products", value: data?.available_products ?? "—", icon: Boxes, color: "text-sambiva-charcoal bg-sambiva-yellow/20" },
  ];

  return (
    <PortalLayout title="Dashboard">
      <div className="mb-8">
        <h2 className="font-serif text-2xl text-sambiva-charcoal">Welcome, {data?.company_name || "Partner"}</h2>
        <p className="text-sambiva-muted mt-1">Account Status: <StatusBadge status={data?.account_status || "Approved"} /></p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-6 border border-sambiva-charcoal/5">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${c.color}`}><c.icon size={22} /></div>
            <p className="text-3xl font-bold text-sambiva-charcoal">{c.value}</p>
            <p className="text-sm text-sambiva-muted mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <button data-testid="qa-catalogue" onClick={() => navigate("/portal/catalogue")} className="flex items-center justify-between bg-sambiva-red text-white rounded-2xl p-5 font-semibold hover:bg-sambiva-deepred transition-colors">Browse Trade Catalogue <ArrowRight size={18} /></button>
        <button data-testid="qa-new-order" onClick={() => navigate("/portal/catalogue")} className="flex items-center justify-between bg-sambiva-green text-white rounded-2xl p-5 font-semibold hover:bg-sambiva-deepgreen transition-colors">Create New Order <PlusCircle size={18} /></button>
        <button data-testid="qa-orders" onClick={() => navigate("/portal/orders")} className="flex items-center justify-between bg-white border border-sambiva-charcoal/10 text-sambiva-charcoal rounded-2xl p-5 font-semibold hover:bg-sambiva-cream transition-colors">View Orders <ArrowRight size={18} /></button>
      </div>

      <h3 className="font-serif text-xl text-sambiva-charcoal mb-4">Recent Orders</h3>
      <div className="bg-white rounded-2xl border border-sambiva-charcoal/5 overflow-hidden">
        {(data?.recent_orders || []).length === 0 ? (
          <div className="p-10 text-center text-sambiva-muted">No orders yet. Your Sambiva orders will appear here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-sambiva-cream text-sambiva-muted text-left">
              <tr><th className="px-5 py-3 font-semibold">Order</th><th className="px-5 py-3 font-semibold">Date</th><th className="px-5 py-3 font-semibold">Total</th><th className="px-5 py-3 font-semibold">Status</th></tr>
            </thead>
            <tbody>
              {data.recent_orders.map((o) => (
                <tr key={o.id} data-testid={`recent-order-${o.order_number}`} onClick={() => navigate(`/portal/orders/${o.id}`)} className="border-t border-sambiva-charcoal/5 hover:bg-sambiva-cream/50 cursor-pointer">
                  <td className="px-5 py-3 font-medium text-sambiva-charcoal">{o.order_number}</td>
                  <td className="px-5 py-3 text-sambiva-muted">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-sambiva-charcoal">€{o.total.toFixed(2)}</td>
                  <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PortalLayout>
  );
}
